import {
  BadgeCheck,
  Dices,
  KeyRound,
  Link2,
  Loader2,
  Mail,
  RefreshCw,
  Save,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MIN_PASSWORT_LAENGE, type Role } from '../../../shared/validation';
import { adminApi, type AdminApp, type AdminUser } from '../../lib/api';
import { Meldung } from '../../lib/Meldung';
import { PasswortSetzenSheet, type PasswortZiel } from '../../components/PasswortSetzenSheet';
import { ZugangSheet } from '../../components/ZugangSheet';
import { abgleichen } from '../../lib/peopleAbgleich';
import { useEmployees, type Employee } from '../../lib/useEmployees';

const dateTime = new Intl.DateTimeFormat('de-DE', { dateStyle: 'short', timeStyle: 'short' });

export function UsersPanel() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [apps, setApps] = useState<AdminApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  /**
   * Ungespeicherte Rollenaenderungen, je Person und App.
   *
   * Vorher ging jede Auswahl sofort ans Backend und danach wurde die ganze
   * Liste neu geladen — die Seite sprang bei jedem Klick nach oben, und wer
   * einer Person fuenf Apps zuteilen wollte, machte das fuenfmal mit. Jetzt
   * sammeln sich die Aenderungen hier und gehen auf einen Knopf raus.
   */
  const [entwurf, setEntwurf] = useState<Record<string, Record<string, string>>>({});
  const [einladung, setEinladung] = useState<{ email: string; link: string; versendet: boolean } | null>(null);

  /**
   * Der Einladungsblock steht oben, die Knoepfe stehen in der Tabelle darunter.
   * Ab einer Handvoll Personen klickt man auf "Link anzeigen" und sieht nichts
   * passieren — der Link erscheint ausserhalb des Sichtfelds. Deshalb holen wir
   * ihn nach dem Erzeugen in den Blick.
   */
  /**
   * Frisch vergebene Zugangsdaten. Stehen genau einmal hier — die Function
   * gibt das Passwort in ihrer Antwort zurueck und speichert es nirgends.
   * Wer den Block wegklickt, kommt nur ueber ein neues Passwort wieder heran.
   */
  const [zugang, setZugang] = useState<{
    email: string | null;
    passwort: string;
    neu: boolean;
  } | null>(null);

  const { state: peopleState, laden: peopleLaden } = useEmployees();

  /**
   * Wer aus People hat noch keinen Zugang? Wird bei jedem Rendern neu
   * gerechnet — die Listen sind klein, und nach dem Anlegen eines Kontos muss
   * die Zeile sofort verschwinden.
   */
  const abgleich =
    peopleState.status === 'ready' ? abgleichen(peopleState.employees, users) : null;

  /*
   * `error` und `info` bleiben als getrennte Zustaende — alle Handler setzen
   * sie bereits. Hier werden sie nur zu einer Meldung zusammengefuehrt, weil
   * nie mehr als eine gleichzeitig sichtbar sein soll (§6 Design-System).
   * Der Fehler hat Vorrang: er ist die Nachricht, die man nicht verpassen darf.
   */
  const meldung = error
    ? ({ art: 'error', text: error } as const)
    : info
      ? ({ art: 'success', text: info } as const)
      : null;

  // useCallback, damit der Ausblend-Timer in `Meldung` nicht bei jedem Rendern
  // neu startet und die Meldung dadurch nie von allein verschwindet.
  const meldungSchliessen = useCallback(() => {
    setError(null);
    setInfo(null);
  }, []);

  /**
   * Vorschlag fuer das Feld — `crypto.getRandomValues`, nicht `Math.random`.
   * Dieselben verwechslungsarmen Zeichen wie serverseitig: das Passwort wird
   * vorgelesen oder in einen Chat kopiert.
   */
  function passwortVorschlagen() {
    const zeichen = 'abcdefghijkmnpqrstuvwxyzACBDEFGHJKLMNPQRTUVWXYZ2346789';
    const werte = new Uint32Array(16);
    crypto.getRandomValues(werte);
    const roh = [...werte].map((v) => zeichen[v % zeichen.length]);
    setPasswort(
      [roh.slice(0, 4), roh.slice(4, 8), roh.slice(8, 12), roh.slice(12, 16)]
        .map((g) => g.join(''))
        .join('-'),
    );
  }

  /** Offener Dialog „Passwort setzen" — null = keiner. */
  const [passwortZiel, setPasswortZiel] = useState<PasswortZiel | null>(null);

  const einladungRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // Nur noch fuer den Einladungslink: die Zugangsdaten stehen jetzt im
    // Dialog und brauchen kein Scrollen. Das Springen nach oben war genau
    // das, was wie "nichts passiert" aussah — der Block lag oben, der Klick
    // kam von unten aus der Liste.
    if (!einladung) return;
    einladungRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [einladung]);

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  /*
   * Leer = die Function erzeugt eines. Gefuellt = dieses wird genommen.
   * Steht als Klartext im Feld, nicht als `type="password"`: der Admin muss
   * lesen koennen, was er gleich weitergibt, und verdeckt tippt man sich
   * genau hier vertippt.
   */
  const [passwort, setPasswort] = useState('');
  const [inviting, setInviting] = useState(false);

  /**
   * Laedt die Liste neu — und raeumt dabei **keine** Meldungen ab.
   *
   * Vorher stand hier ein `setError(null)` im Erfolgsfall. Das hat den
   * Hinweis "Konto angelegt, aber keine Mail verschickt" wieder geloescht,
   * bevor ihn jemand lesen konnte: `invite()` setzt ihn und laedt direkt
   * danach neu. Nach aussen sah ein fehlgeschlagener Versand deshalb aus wie
   * ein geglueckter. Wer einen sauberen Zustand will, setzt ihn selbst
   * zurueck — `invite()` und `einladungErneut()` tun das zu Beginn.
   */
  async function reload() {
    setLoading(true);
    setEntwurf({});
    try {
      const data = await adminApi.listUsers();
      setUsers(data.users);
      setApps(data.apps);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function invite(event: React.FormEvent) {
    event.preventDefault();
    setInviting(true);
    setError(null);
    setInfo(null);
    try {
      const res = await adminApi.invite(email.trim(), displayName.trim());
      if (res.link) setEinladung({ email: res.email, link: res.link, versendet: res.versendet });
      if (res.versendet) setInfo(`Einladung an ${res.email} verschickt.`);
      else setError(`Konto angelegt, aber keine Mail verschickt${res.hinweis ? `: ${res.hinweis}` : '.'} Nutze den Link unten.`);
      setEmail('');
      setDisplayName('');
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setInviting(false);
    }
  }

  async function toggleActive(user: AdminUser) {
    setPending(user.id);
    setError(null);
    setInfo(null);
    try {
      await adminApi.setActive(user.id, !user.is_active);
      // Nur die betroffene Zeile anfassen: ein vollstaendiges Neuladen wuerde
      // die Liste neu aufbauen und die Seite an den Anfang springen lassen.
      setUsers((liste) =>
        liste.map((u) => (u.id === user.id ? { ...u, is_active: !user.is_active } : u)),
      );
      setInfo(
        user.is_active
          ? `${user.email ?? 'Der Zugang'} ist gesperrt. Anmelden ist nicht mehr moeglich; eine offene Sitzung endet spaetestens nach einer Stunde.`
          : `${user.email ?? 'Der Zugang'} ist wieder freigegeben.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(null);
    }
  }

  /** Aktuell gewaehlte Rolle: erst der Entwurf, sonst der gespeicherte Stand. */
  function rolleVon(user: AdminUser, appId: string): string {
    const offen = entwurf[user.id]?.[appId];
    if (offen !== undefined) return offen;
    return user.permissions.find((p) => p.app_id === appId)?.role ?? 'none';
  }

  function aendern(userId: string, appId: string, value: string) {
    setEntwurf((alt) => ({ ...alt, [userId]: { ...(alt[userId] ?? {}), [appId]: value } }));
  }

  /** Wieviele Aenderungen warten bei dieser Person auf das Speichern? */
  function offeneAenderungen(user: AdminUser): [string, string][] {
    const offen = entwurf[user.id];
    if (!offen) return [];
    return Object.entries(offen).filter(([appId, wert]) => {
      const gespeichert = user.permissions.find((p) => p.app_id === appId)?.role ?? 'none';
      return wert !== gespeichert;
    });
  }

  async function speichern(user: AdminUser) {
    const aenderungen = offeneAenderungen(user);
    if (aenderungen.length === 0) return;

    setPending(user.id);
    setError(null);
    setInfo(null);
    try {
      // Nacheinander statt parallel: die Function prueft beim Entziehen der
      // letzten Admin-Rolle den Gesamtbestand. Gleichzeitige Aufrufe koennten
      // sich gegenseitig fuer unbedenklich halten.
      for (const [appId, wert] of aenderungen) {
        await adminApi.setPermission(user.id, appId, wert === 'none' ? null : (wert as Role));
      }

      // Zeile lokal nachziehen, Liste bleibt stehen.
      setUsers((liste) =>
        liste.map((u) => {
          if (u.id !== user.id) return u;
          const rest = u.permissions.filter(
            (p) => !aenderungen.some(([appId]) => appId === p.app_id),
          );
          const neu = aenderungen
            .filter(([, wert]) => wert !== 'none')
            .map(([app_id, role]) => ({ app_id, role }));
          return { ...u, permissions: [...rest, ...neu] };
        }),
      );
      setEntwurf((alt) => {
        const kopie = { ...alt };
        delete kopie[user.id];
        return kopie;
      });
      setInfo(
        `${aenderungen.length} ${aenderungen.length === 1 ? 'Berechtigung' : 'Berechtigungen'} für ${user.email ?? 'den Benutzer'} gespeichert.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(null);
    }
  }

  /**
   * Konto anlegen und Zugangsdaten aushaendigen — der Weg, der ohne
   * funktionierenden Mailversand auskommt.
   *
   * Nutzt dieselben Formularfelder wie das Einladen; was sich unterscheidet,
   * ist allein, wer das Passwort setzt.
   */
  async function anlegenMitZugang() {
    if (!email.trim()) {
      setError('Ohne E-Mail-Adresse geht es nicht.');
      return;
    }
    if (passwort && passwort.length < MIN_PASSWORT_LAENGE) {
      setError(`Das Passwort braucht mindestens ${MIN_PASSWORT_LAENGE} Zeichen.`);
      return;
    }
    setInviting(true);
    setError(null);
    setInfo(null);
    setEinladung(null);
    setZugang(null);
    try {
      const res = await adminApi.createUser(
        email.trim(),
        displayName.trim(),
        passwort || undefined,
      );
      setZugang({ email: res.email, passwort: res.passwort, neu: true });
      setEmail('');
      setDisplayName('');
      setPasswort('');
      await reload();
      setInfo(`Konto fuer ${res.email} angelegt.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setInviting(false);
    }
  }

  /**
   * Zugang fuer jemanden aus dem Personalstamm anlegen.
   *
   * Setzt die `zoho_user_id` mit — nur dadurch bleibt die Zuordnung bestehen,
   * wenn sich spaeter die E-Mail-Adresse aendert.
   */
  async function ausPeopleAnlegen(mitarbeiter: Employee) {
    if (!mitarbeiter.email) {
      setError(
        `Fuer ${mitarbeiter.name} steht in People keine E-Mail-Adresse. Ohne die geht kein Zugang — bitte dort nachtragen.`,
      );
      return;
    }
    setPending(`people:${mitarbeiter.id}`);
    setError(null);
    setInfo(null);
    setEinladung(null);
    setZugang(null);
    try {
      const res = await adminApi.createUser(
        mitarbeiter.email,
        mitarbeiter.name,
        undefined,
        mitarbeiter.id,
      );
      setZugang({ email: res.email, passwort: res.passwort, neu: true });
      await reload();
      setInfo(`Konto fuer ${res.email} angelegt.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(null);
    }
  }

  /**
   * Anmeldung freigeben. Nach dem Setzen eines Passworts passiert das von
   * selbst — dieser Knopf ist fuer Konten, die ihr Passwort schon haben.
   */
  async function anmeldungFreigeben(user: AdminUser) {
    setPending(`${user.id}:freigeben`);
    setError(null);
    setInfo(null);
    try {
      await adminApi.confirmUser(user.id);
      setUsers((liste) =>
        liste.map((u) => (u.id === user.id ? { ...u, confirmed: true } : u)),
      );
      setInfo(`${user.email ?? 'Das Konto'} kann sich jetzt anmelden.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(null);
    }
  }

  /**
   * Konto endgueltig loeschen. Zwei Ruecktritte: die Nachfrage und der
   * Hinweis, dass Sperren in den meisten Faellen reicht.
   */
  async function loeschen(user: AdminUser) {
    const bezeichnung = user.email ?? user.display_name ?? 'dieses Konto';
    if (
      !window.confirm(
        `${bezeichnung} endgueltig loeschen?

Das Konto und alle Berechtigungen sind danach weg und lassen sich nicht zurueckholen. Wenn die Person spaeter wiederkommen koennte, ist "Sperren" der bessere Weg.`,
      )
    ) {
      return;
    }
    setPending(`${user.id}:loeschen`);
    setError(null);
    setInfo(null);
    try {
      await adminApi.deleteUser(user.id);
      setUsers((liste) => liste.filter((u) => u.id !== user.id));
      setInfo(`${bezeichnung} wurde geloescht.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(null);
    }
  }

  /**
   * Oeffnet den Dialog. Das Passwort wird dort eingegeben oder erzeugt —
   * frueher lief das ohne Rueckfrage durch, und man bekam eines zugeteilt,
   * ohne eines vergeben zu koennen.
   */
  function passwortNeu(user: AdminUser) {
    setError(null);
    setInfo(null);
    setEinladung(null);
    setZugang(null);
    setPasswortZiel({ id: user.id, bezeichnung: user.email ?? user.display_name ?? 'das Konto' });
  }

  async function passwortSetzen(passwort: string) {
    const ziel = passwortZiel;
    if (!ziel) return;
    setPending(`${ziel.id}:passwort`);
    setError(null);
    try {
      const res = await adminApi.resetPassword(ziel.id, passwort);
      // Die Function bestaetigt die Adresse gleich mit; ohne das kaeme die
      // Person trotz richtigem Passwort nicht herein.
      setUsers((liste) => liste.map((u) => (u.id === ziel.id ? { ...u, confirmed: true } : u)));
      setPasswortZiel(null);
      setZugang({ email: res.email ?? ziel.bezeichnung, passwort: res.passwort, neu: false });
      setInfo(`Neues Passwort fuer ${res.email ?? ziel.bezeichnung} gesetzt.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPasswortZiel(null);
    } finally {
      setPending(null);
    }
  }

  /**
   * `nurLink` erzeugt den Link, ohne zu mailen — fuer den Fall, dass der Admin
   * ihn selbst weitergeben will. Sonst wird gemailt **und** der Link gezeigt:
   * eine Mail kann im Spam landen, und dann wartet sonst jemand vergeblich.
   */
  async function einladungErneut(user: AdminUser, nurLink = false) {
    setPending(`${user.id}:invite`);
    setError(null);
    setInfo(null);
    setEinladung(null);
    try {
      const res = await adminApi.resendInvite(user.id, nurLink);
      if (res.link) setEinladung({ email: res.email, link: res.link, versendet: res.versendet });
      // Ohne Link ist der Knopf wirkungslos — und ohne Meldung sieht es aus,
      // als haette man danebengeklickt. Lieber sagen, dass nichts kam.
      else setError(
        `Es kam kein Einladungslink zurueck${res.hinweis ? `: ${res.hinweis}` : '.'} Bitte erneut versuchen.`,
      );
      if (res.versendet) setInfo(`Einladung an ${res.email} verschickt.`);
      else if (!nurLink && res.hinweis) setError(`Mail nicht verschickt: ${res.hinweis}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(null);
    }
  }

  return (
    <div className="stack">
      <section className="card">
        <h2>Benutzer einladen</h2>
        <p className="card__meta">
          Die Einladung geht per E-Mail raus; der Account entsteht ohne Self-Signup.
        </p>
        <form onSubmit={invite}>
          <div className="form__row form__row--2">
            <label className="field">
              <span className="field__label">E-Mail</span>
              <input
                className="input"
                type="email"
                required
                value={email}
                inputMode="email"
                autoCapitalize="none"
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label className="field">
              <span className="field__label">Name (optional)</span>
              <input
                className="input"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </label>
            <label className="field">
              <span className="field__label">
                Passwort (leer lassen = wird erzeugt, sonst min. {MIN_PASSWORT_LAENGE} Zeichen)
              </span>
              <div className="form__actions">
                <input
                  className="input mono"
                  type="text"
                  value={passwort}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="wird erzeugt"
                  onChange={(e) => setPasswort(e.target.value)}
                />
                <button
                  type="button"
                  className="px-btn px-btn--ghost px-btn--sm"
                  onClick={passwortVorschlagen}
                  title="Ein zufaelliges Passwort einsetzen"
                >
                  <Dices size={14} aria-hidden="true" />
                  Erzeugen
                </button>
              </div>
            </label>
          </div>
          <div className="form__actions">
            <button
              type="button"
              className="px-btn px-btn--sm"
              disabled={inviting}
              onClick={() => void anlegenMitZugang()}
              title="Konto anlegen und ein Passwort aushaendigen — ohne E-Mail"
            >
              {inviting ? (
                <Loader2 size={16} className="spin" aria-hidden="true" />
              ) : (
                <KeyRound size={16} aria-hidden="true" />
              )}
              Mit Zugangsdaten anlegen
            </button>
            <button
              type="submit"
              className="px-btn px-btn--ghost px-btn--sm"
              disabled={inviting}
              title="Einladung per E-Mail — die Person setzt ihr Passwort selbst"
            >
              <UserPlus size={16} aria-hidden="true" />
              Stattdessen einladen
            </button>
          </div>
        </form>
      </section>

      <Meldung inhalt={meldung} onClose={meldungSchliessen} />
      <PasswortSetzenSheet
        ziel={passwortZiel}
        laeuft={pending === `${passwortZiel?.id}:passwort`}
        onSetzen={(pw) => void passwortSetzen(pw)}
        onClose={() => setPasswortZiel(null)}
      />
      <ZugangSheet zugang={zugang} onClose={() => setZugang(null)} />

      {einladung && (
        <div className="alert" role="status" ref={einladungRef}>
          <p>
            {einladung.versendet
              ? `Die Mail an ${einladung.email} ist raus. Falls sie nicht ankommt, kannst du diesen Link weitergeben:`
              : `Kein Versand an ${einladung.email}. Der folgende Link gilt einmalig — bitte selbst weitergeben:`}
          </p>
          <input
            className="input"
            readOnly
            value={einladung.link}
            onFocus={(e) => e.currentTarget.select()}
            aria-label="Einladungslink"
            style={{ marginTop: '.5rem' }}
          />
        </div>
      )}

      <section className="card">
        <div className="section__head">
          <h2>Aus People uebernehmen</h2>
          <button
            type="button"
            className="px-btn px-btn--ghost px-btn--sm"
            onClick={peopleLaden}
            disabled={peopleState.status === 'loading' || peopleState.status === 'unconfigured'}
          >
            {peopleState.status === 'loading' ? (
              <Loader2 size={14} className="spin" aria-hidden="true" />
            ) : (
              <RefreshCw size={14} aria-hidden="true" />
            )}
            {peopleState.status === 'ready' ? 'Neu laden' : 'Personalstamm laden'}
          </button>
        </div>

        {peopleState.status === 'unconfigured' && (
          <p className="card__meta">
            Keine Anbindung an People konfiguriert. Ohne sie bleibt nur das Anlegen von Hand.
          </p>
        )}
        {peopleState.status === 'idle' && (
          <p className="card__meta">
            Zeigt, wer in Zoho People steht und noch keinen Hub-Zugang hat. Wird erst auf Klick
            geladen — es sind Personaldaten.
          </p>
        )}
        {peopleState.status === 'error' && (
          <p className="card__meta">{peopleState.message}</p>
        )}

        {abgleich && (
          <>
            <p className="card__meta">
              {abgleich.ohneZugang.length === 0
                ? 'Alle aktiven Mitarbeiter haben einen Zugang.'
                : `${abgleich.ohneZugang.length} ohne Zugang`}
              {` · ${abgleich.verknuepft} verknuepft`}
              {abgleich.ausgetreten > 0 && ` · ${abgleich.ausgetreten} ausgetreten, ausgeblendet`}
            </p>
            {abgleich.ohneZugang.map((m) => (
              <div className="userrow" key={m.id}>
                <div className="userrow__main">
                  <strong>{m.name}</strong>
                  <span className="card__meta">{m.email ?? 'keine E-Mail in People'}</span>
                </div>
                <div className="userrow__actions">
                  <button
                    type="button"
                    className="px-btn px-btn--sm"
                    onClick={() => void ausPeopleAnlegen(m)}
                    disabled={pending === `people:${m.id}` || !m.email}
                    title={
                      m.email
                        ? 'Konto anlegen und Zugangsdaten anzeigen'
                        : 'Ohne E-Mail-Adresse in People nicht moeglich'
                    }
                  >
                    {pending === `people:${m.id}` ? (
                      <Loader2 size={14} className="spin" aria-hidden="true" />
                    ) : (
                      <Users size={14} aria-hidden="true" />
                    )}
                    Zugang anlegen
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </section>

      <section className="card">
        <div className="section__head">
          <h2>Benutzer &amp; Berechtigungen</h2>
          <span className="card__meta">{users.length}</span>
        </div>

        {loading ? (
          <div className="empty">Benutzer werden geladen …</div>
        ) : users.length === 0 ? (
          <div className="empty">Noch keine Benutzer.</div>
        ) : (
          users.map((user) => (
            <div className="userrow" key={user.id}>
              <div className="userrow__top">
                <div>
                  <div className="userrow__mail">{user.email ?? '(keine E-Mail)'}</div>
                  <div className="card__meta">
                    {user.display_name ?? 'kein Name'}
                    {user.last_sign_in_at
                      ? ` · zuletzt ${dateTime.format(new Date(user.last_sign_in_at))}`
                      : ' · noch nicht angemeldet'}
                  </div>
                  <div className="userrow__tags">
                    <span className={`badge-pill${user.is_active ? '' : ' badge-pill--muted'}`}>
                      {user.is_active ? 'aktiv' : 'deaktiviert'}
                    </span>
                    {!user.confirmed && (
                      <span className="badge-pill badge-pill--muted">Anmeldung nicht freigegeben</span>
                    )}
                    {!user.has_profile && <span className="badge-pill badge-pill--pink">kein Profil</span>}
                  </div>
                </div>
                <div className="userrow__actions">
                  {!user.confirmed && (
                    <>
                      <button
                        type="button"
                        className="px-btn px-btn--ghost px-btn--sm"
                        onClick={() => void einladungErneut(user)}
                        disabled={pending === `${user.id}:invite`}
                        title="Einladungsmail erneut zustellen"
                      >
                        {pending === `${user.id}:invite` ? (
                          <Loader2 size={14} className="spin" aria-hidden="true" />
                        ) : (
                          <Mail size={14} aria-hidden="true" />
                        )}
                        Einladung senden
                      </button>
                      <button
                        type="button"
                        className="px-btn px-btn--ghost px-btn--sm"
                        onClick={() => void einladungErneut(user, true)}
                        disabled={pending === `${user.id}:invite`}
                        title="Nur den Link erzeugen, ohne Mail"
                      >
                        <Link2 size={14} aria-hidden="true" />
                        Link anzeigen
                      </button>
                    </>
                  )}
                  {!user.confirmed && (
                    <button
                      type="button"
                      className="px-btn px-btn--ghost px-btn--sm"
                      onClick={() => void anmeldungFreigeben(user)}
                      disabled={pending === `${user.id}:freigeben`}
                      title="Anmeldung freigeben, ohne das Passwort zu aendern"
                    >
                      {pending === `${user.id}:freigeben` ? (
                        <Loader2 size={14} className="spin" aria-hidden="true" />
                      ) : (
                        <BadgeCheck size={14} aria-hidden="true" />
                      )}
                      Anmeldung freigeben
                    </button>
                  )}
                  {/* Steht bewusst ausserhalb der `confirmed`-Bedingung: wer sein
                      Passwort vergisst, hat sein Konto laengst bestaetigt. */}
                  <button
                    type="button"
                    className="px-btn px-btn--ghost px-btn--sm"
                    onClick={() => passwortNeu(user)}
                    disabled={pending === `${user.id}:passwort`}
                    title="Neues Passwort erzeugen und anzeigen"
                  >
                    {pending === `${user.id}:passwort` ? (
                      <Loader2 size={14} className="spin" aria-hidden="true" />
                    ) : (
                      <KeyRound size={14} aria-hidden="true" />
                    )}
                    Passwort neu
                  </button>
                  <button
                    type="button"
                    className={`px-btn px-btn--sm ${user.is_active ? 'px-btn--danger' : 'px-btn--ghost'}`}
                    onClick={() => void toggleActive(user)}
                    disabled={pending === user.id}
                  >
                    {pending === user.id ? '…' : user.is_active ? 'Sperren' : 'Freigeben'}
                  </button>
                  <button
                    type="button"
                    className="px-btn px-btn--danger px-btn--sm"
                    onClick={() => void loeschen(user)}
                    disabled={pending === `${user.id}:loeschen`}
                    title="Konto endgueltig loeschen"
                  >
                    {pending === `${user.id}:loeschen` ? (
                      <Loader2 size={14} className="spin" aria-hidden="true" />
                    ) : (
                      <Trash2 size={14} aria-hidden="true" />
                    )}
                    Loeschen
                  </button>
                </div>
              </div>

              <div className="perms">
                {apps.map((app) => {
                  const aktuell = rolleVon(user, app.id);
                  const gespeichert =
                    user.permissions.find((p) => p.app_id === app.id)?.role ?? 'none';
                  const geaendert = aktuell !== gespeichert;
                  // Welche Rollen diese App kennt, kommt aus `apps.roles`.
                  // Der MPS Manager hat sechs, andere nur zwei.
                  const rollen = app.roles?.length ? app.roles : ['user', 'admin'];
                  return (
                    <div className="perm" key={app.id}>
                      <span className="perm__name" title={app.name}>
                        {app.name}
                      </span>
                      <select
                        className="select"
                        value={aktuell}
                        aria-label={`Rolle für ${app.name}`}
                        disabled={pending === user.id}
                        onChange={(e) => aendern(user.id, app.id, e.target.value)}
                        style={geaendert ? { borderColor: 'var(--accent, #E95297)' } : undefined}
                      >
                        <option value="none">kein Zugriff</option>
                        {rollen.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>

              {offeneAenderungen(user).length > 0 && (
                <div className="userrow__save">
                  <span className="card__meta">
                    {offeneAenderungen(user).length} ungespeicherte{' '}
                    {offeneAenderungen(user).length === 1 ? 'Änderung' : 'Änderungen'}
                  </span>
                  <button
                    type="button"
                    className="px-btn px-btn--sm"
                    onClick={() => void speichern(user)}
                    disabled={pending === user.id}
                  >
                    {pending === user.id ? (
                      <Loader2 size={14} className="spin" aria-hidden="true" />
                    ) : (
                      <Save size={14} aria-hidden="true" />
                    )}
                    Speichern
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </section>
    </div>
  );
}
